// structures.bin (STR1) — the baker-2 structure artifact (APP_BAKER §3 "One collider"):
// ONE triangle soup, grouped per structure, that is both DRAWN (S7, per-chunk
// material batches) and COLLIDED (game/structcol.js). PURE module shared by
// the WORLD stage (require), the game server (require), the WebXR client
// (esbuild import) and the gates — the lifts.js/spans.js mould: dependency-
// free, no fs, no THREE. Spec: game/protocol/PROTOCOL.md §structures.bin.
//
// Layout (little-endian body, big-endian magic like SPN1/SOL1):
//   header 28 B   'STR1' u16 version u16 flags(0) u32 nStruct u32 nVerts
//                 u32 nTris f32 chunk(m, 0 = unchunked) u32 nChunks
//   chunks        nChunks x 12 B: i16 cx i16 cz u32 firstStruct u32 nStruct
//   structures    nStruct x 48 B: u8 kind u8 prov u8 srcKind u8 0
//                 u32 srcIndex u32 firstVert u32 nVerts u32 firstTri u32 nTris
//                 f32 bbox[6] (x0 y0 z0 x1 y1 z1)
//   verts         nVerts x 3 f32 (x y z, absolute game metres)
//   idx           nTris x 3 u32 (absolute vertex index, inside the owner's range)
//   mat           nTris u8   (MATS index, 255 = none)
//   tflags        nTris u8   (TF bits)
//   pad           0..3 zero bytes to a 4-byte boundary
//   checksum      u32 FNV-1a over every preceding byte
// Every section starts 4-aligned, so a loader on a little-endian host maps
// verts/idx as typed-array views without copying.
//
// Structures are sorted by chunk (the chunk of their bbox centre), and their
// vertices and triangles are stored in that order, so ONE chunk is ONE
// contiguous triangle range — S7 draws a chunk as a slice, S6 splits a long
// structure into per-chunk pieces (each piece its own record, same src).
//
// The reader FAILS FAST: any structural error throws (the server treats that
// as fatal, like checkWays). A MISSING file is not an error — it is an empty
// collider (baker 2 before S6 emits anything).
'use strict';

const STR_MAGIC = 0x53545231;   // 'STR1'
// Format version. Lives in the file header, NOT the rev registry: S5 only
// defines the format and nothing bakes it yet. When S6 makes the WORLD stage
// emit it, the world rev bump carries it (pipeline/revs.json), and any change
// to this layout bumps STR_VERSION here in the same change.
const STR_VERSION = 1;
const HEADER_BYTES = 28;
const CHUNK_BYTES = 12;
const STRUCT_BYTES = 48;

// structure kind (u8). Append only — the index is the wire value.
const KINDS = ['other', 'bore-lining', 'portal', 'deck', 'girder', 'pier',
  'abutment', 'parapet', 'underpass', 'retaining-wall', 'embankment',
  'cut-slope', 'gallery', 'stair', 'junction', 'apron', 'fixture',
  // (S6.d M4) the gallery's interior face: one-sided toward the interior,
  // the collision of the pair; kind gallery is then the outer face
  'gallery-lining',
  // (S6.d B4c-3) TWO PORTALS CLOSER THAN A SECTION ARE ONE MOUTH: the one
  // room both tubes open into (world/emit.js chambersOf)
  'chamber',
  // (S6.d B4c-5 P-1c, main's ruling 1) THE FELT SKIN PAST THE UNION IS A
  // RECORD OF THE DRAWN SKIN: a collide-only copy of the drawn skin's quads
  // outside the union of the bores' cut volumes (world/emit.js
  // skinPastUnion): every triangle WALK | NO_RENDER, no material, no new
  // height — never drawn, never a wall
  'skin-copy',
  // (WAYS-G W-g) A LOW FILL ON STEEP GROUND IS A CONCRETE BED: the fill
  // body of a corridor concrete-bed record (world/fills.js), a fill
  // to every reader that reads a fill
  'concrete-bed',
  // (WAYS-G A3) the funicular's open steel track structure (rails on steel
  // sleepers / girders on low supports with footings, see-through,
  // alpha-tested in client/src/structs2.js) and its metal side stairs
  // (grating treads, stringers, handrail; walkable)
  'funicular-track', 'funicular-stair'];
// provenance (u8) — APP_BAKER §3 "One collider"
const PROV = ['measured', 'fitted', 'solved', 'declared', 'declared-generic'];
// which ways.json (vrm-ways schema 2) top-level array srcIndex points into;
// 'none' = srcIndex 0xFFFFFFFF (a fixture, a generic kit piece)
// (S6.d B4c-5 P6) 'halls': ways.json halls[] (a building hall's floor record,
// world/halls.js). (P2a, APP_ARCHITECT §16) 'sites': ways.json sites[]
// (an architect site's built record, world/planbuild.js via world/emit.js
// siteBuild). Append only — the index is the wire value.
const SRC = ['none', 'ways', 'structures', 'bores', 'links', 'declared', 'flights', 'decks', 'halls', 'sites'];
const SRC_NONE = 0xFFFFFFFF;
// per-triangle material (u8) — the S7 draw batches (§3.7). 255 = none, legal
// only on a NO_RENDER (collide-only) triangle.
const MATS = ['concrete', 'stone', 'tarmac', 'ballast', 'lining', 'steel',
  'gravel', 'timber', 'earth',
  // (WAYS-G A3) open steel grating (see-through treads / walkways)
  'grating',
  // (deck-top-is-its-way) a deck top carrying a paving / sett way
  'paving', 'sett',
  // (retaining-wall-stone, owner 2026-10-13) a retaining wall's natural-stone face + its coping
  'masonry', 'granite',
  // (bridge railings, owner 2026-10-16) a road railing's galvanized W-beam
  // guard rail (emit.deck.railing.style.guard)
  'galvanized',
  // (bridge-body-mat, owner 2026-10-16: "train: dark steel") a rail deck's
  // girders, soffit and piers (emit.deck.mat.body.rail)
  'steel_dark'];
const MAT_NONE = 255;
// (WAYS-G, ruling: append only) THE WIRE INDICES NEVER MOVE: the enums as
// they stood before WAYS-G, checked at load — an insert or a reorder above
// the appended tail fails here, before a file is written or read
const KINDS_FROZEN = ['other', 'bore-lining', 'portal', 'deck', 'girder', 'pier', 'abutment', 'parapet', 'underpass', 'retaining-wall',
  'embankment', 'cut-slope', 'gallery', 'stair', 'junction', 'apron', 'fixture', 'gallery-lining', 'chamber', 'skin-copy'];
const MATS_FROZEN = ['concrete', 'stone', 'tarmac', 'ballast', 'lining', 'steel', 'gravel', 'timber', 'earth'];
for (const [A, F, w] of [[KINDS, KINDS_FROZEN, 'KINDS'], [MATS, MATS_FROZEN, 'MATS']]) {
  F.forEach((k, i) => { if (A[i] !== k) throw new Error(`game/structures.js ${w}[${i}] is ${A[i]}, the wire value of ${k}: the enum is append only`); });
}
// per-triangle flags (u8)
const TF = {
  WALK: 1,        // a standing surface (ground query) — if not steeper than the slope limit
  WALL: 2,        // a lateral blocker (informative: EVERY collidable tri blocks in the body band)
  CEIL: 4,        // clamps the head from below (bore crown, deck underside)
  STAIR: 8,       // WALK at the stair slope limit (45°) instead of slope_walk_max_deg
  NO_COLLIDE: 16, // render-only (a railing mesh drawn but walked through)
  NO_RENDER: 32,  // collide-only (an invisible guard, a coarse proxy)
  TWO_SIDED: 64,  // render hint: no backface cull. Collision is ALWAYS two-sided.
  LIGHT: 128,     // render hint (P3d): an emitter, drawn at its own colour unlit (a site's light strip; APP_ARCHITECT §18 P3d)
};
const TF_ROLE = TF.WALK | TF.WALL | TF.CEIL;
const KIND_PARAPET = KINDS.indexOf('parapet');
const TF_KNOWN = 0xFF;

// FNV-1a 32 over bytes [from, n), continuing from hash h (the default: the
// whole prefix [0, n) from the offset basis)
function fnv1a(u8, n, from = 0, h = 0x811C9DC5) {
  for (let i = from; i < n; i++) {
    h ^= u8[i];
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

const _le = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1;

function chunkOf(x, z, chunk) {
  return chunk > 0 ? [Math.floor(x / chunk), Math.floor(z / chunk)] : [0, 0];
}

// spec -> Uint8Array (write it with fs.writeFileSync). spec:
//   { chunk: metres (0 = one chunk), structures: [{
//       kind: 'deck' | index, prov: 'solved' | index,
//       src: { kind: 'structures' | index, index } | null,
//       verts: flat [x,y,z,...] (Float32Array or array),
//       tris: flat [a,b,c,...] LOCAL vertex indices (0-based per structure),
//       mat: per-tri array OR one value for all (name or index),
//       flags: per-tri array OR one value for all (TF bits) }] }
// Throws on a spec the reader would reject — a writer can never emit a file
// the fail-fast reader refuses.
function serializeStructures(spec) {
  const chunk = +(spec && spec.chunk) || 0;
  const enumIdx = (v, list, what) => {
    const i = typeof v === 'number' ? v : list.indexOf(v);
    if (!(i >= 0 && i < list.length)) throw new Error(`structures: bad ${what} ${v}`);
    return i;
  };
  const matIdx = (v) => (v === MAT_NONE || v === 'none' ? MAT_NONE : enumIdx(v, MATS, 'material'));
  const recs = (spec.structures || []).map((s, i) => {
    const nv = s.verts.length / 3, nt = s.tris.length / 3;
    if (!Number.isInteger(nv) || !Number.isInteger(nt) || nv < 3 || nt < 1) {
      throw new Error(`structures: structure ${i} needs >= 3 verts and >= 1 tri`);
    }
    const bb = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
    for (let v = 0; v < nv; v++) {
      for (let c = 0; c < 3; c++) {
        const q = Math.fround(s.verts[v * 3 + c]);
        if (!Number.isFinite(q)) throw new Error(`structures: structure ${i} vertex not finite`);
        if (q < bb[c]) bb[c] = q;
        if (q > bb[c + 3]) bb[c + 3] = q;
      }
    }
    const [cx, cz] = chunkOf((bb[0] + bb[3]) / 2, (bb[2] + bb[5]) / 2, chunk);
    if (cx < -32768 || cx > 32767 || cz < -32768 || cz > 32767) {
      throw new Error(`structures: structure ${i} chunk out of i16 range`);
    }
    const src = s.src || null;
    return {
      i, s, nv, nt, bb, cx, cz,
      kind: enumIdx(s.kind ?? 0, KINDS, 'kind'),
      prov: enumIdx(s.prov ?? 0, PROV, 'provenance'),
      srcKind: src ? enumIdx(src.kind, SRC, 'src kind') : 0,
      srcIndex: src && src.kind !== 'none' && src.kind !== 0 ? src.index >>> 0 : SRC_NONE,
    };
  });
  // chunk order (cz, cx), stable within a chunk
  recs.sort((a, b) => (a.cz - b.cz) || (a.cx - b.cx) || (a.i - b.i));
  const chunks = [];
  for (let k = 0; k < recs.length; k++) {
    const r = recs[k];
    const last = chunks[chunks.length - 1];
    if (last && last.cx === r.cx && last.cz === r.cz) last.n++;
    else chunks.push({ cx: r.cx, cz: r.cz, first: k, n: 1 });
  }
  let nVerts = 0, nTris = 0;
  for (const r of recs) { nVerts += r.nv; nTris += r.nt; }
  const body = HEADER_BYTES + chunks.length * CHUNK_BYTES + recs.length * STRUCT_BYTES
    + nVerts * 12 + nTris * 12 + nTris * 2;
  const pad = (4 - (body % 4)) % 4;
  const total = body + pad + 4;
  const buf = new ArrayBuffer(total);
  const dv = new DataView(buf);
  const u8 = new Uint8Array(buf);
  dv.setUint32(0, STR_MAGIC, false);
  dv.setUint16(4, STR_VERSION, true);
  dv.setUint16(6, 0, true);
  dv.setUint32(8, recs.length, true);
  dv.setUint32(12, nVerts, true);
  dv.setUint32(16, nTris, true);
  dv.setFloat32(20, chunk, true);
  dv.setUint32(24, chunks.length, true);
  let o = HEADER_BYTES;
  for (const c of chunks) {
    dv.setInt16(o, c.cx, true); dv.setInt16(o + 2, c.cz, true);
    dv.setUint32(o + 4, c.first, true); dv.setUint32(o + 8, c.n, true);
    o += CHUNK_BYTES;
  }
  let fv = 0, ft = 0;
  for (const r of recs) {
    dv.setUint8(o, r.kind); dv.setUint8(o + 1, r.prov);
    dv.setUint8(o + 2, r.srcKind); dv.setUint8(o + 3, 0);
    dv.setUint32(o + 4, r.srcIndex, true);
    dv.setUint32(o + 8, fv, true); dv.setUint32(o + 12, r.nv, true);
    dv.setUint32(o + 16, ft, true); dv.setUint32(o + 20, r.nt, true);
    for (let c = 0; c < 6; c++) dv.setFloat32(o + 24 + c * 4, r.bb[c], true);
    o += STRUCT_BYTES;
    r.fv = fv; r.ft = ft;
    fv += r.nv; ft += r.nt;
  }
  for (const r of recs) {
    for (let k = 0; k < r.nv * 3; k++) { dv.setFloat32(o, r.s.verts[k], true); o += 4; }
  }
  for (const r of recs) {
    for (let k = 0; k < r.nt * 3; k++) {
      const v = r.s.tris[k];
      if (!(Number.isInteger(v) && v >= 0 && v < r.nv)) {
        throw new Error(`structures: structure ${r.i} tri index ${v} outside its ${r.nv} verts`);
      }
      dv.setUint32(o, r.fv + v, true); o += 4;
    }
  }
  const perTri = (r, v, t) => (Array.isArray(v) || ArrayBuffer.isView(v) ? v[t] : v);
  for (const r of recs) {
    for (let t = 0; t < r.nt; t++) u8[o++] = matIdx(perTri(r, r.s.mat ?? 0, t));
  }
  for (const r of recs) {
    for (let t = 0; t < r.nt; t++) {
      u8[o++] = perTri(r, r.s.flags ?? TF.WALL, t);
    }
  }
  o += pad;
  dv.setUint32(o, fnv1a(u8, o), true);
  // round-trip through the reader: the writer may never emit what it rejects
  loadStructures(u8);
  return u8;
}

// ArrayBuffer | TypedArray | Buffer -> structures object. THROWS on anything
// malformed (fail fast: a half-read collider is worse than none).
// loadStructures = drain(loadSteps): the reader as a STEP GENERATOR
// (client-load-unresponsive) — it yields between slices of the checksum and
// of its validation loops, so the client (client/src/decode.js
// decodeStructuresSteps) spreads a 10 MB file over main-thread tasks. The
// same checks in the same order (the same first failure), the same record.
function loadStructures(buf) {
  const it = loadSteps(buf);
  for (;;) { const r = it.next(); if (r.done) return r.value; }
}
const STEP_BYTES = 1 << 20, STEP_STRUCTS = 1023, STEP_VALS = (1 << 20) - 1;   // masks: 2^k - 1
function* loadSteps(buf) {
  let u8 = buf instanceof ArrayBuffer ? new Uint8Array(buf)
    : new Uint8Array(buf.buffer, buf.byteOffset || 0, buf.byteLength);
  if (u8.byteOffset % 4) u8 = u8.slice();   // pooled Node Buffer: realign
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  const n = u8.byteLength;
  const fail = (m) => { throw new Error(`structures.bin: ${m}`); };
  if (n < HEADER_BYTES + 4) fail(`truncated (${n} B)`);
  if (dv.getUint32(0, false) !== STR_MAGIC) fail('bad magic (not STR1)');
  const version = dv.getUint16(4, true);
  if (version !== STR_VERSION) fail(`version ${version}, this reader is ${STR_VERSION}`);
  if (dv.getUint16(6, true) !== 0) fail('reserved header flags set');
  const nStruct = dv.getUint32(8, true);
  const nVerts = dv.getUint32(12, true);
  const nTris = dv.getUint32(16, true);
  const chunk = dv.getFloat32(20, true);
  const nChunks = dv.getUint32(24, true);
  if (!(chunk >= 0 && Number.isFinite(chunk))) fail(`bad chunk size ${chunk}`);
  const body = HEADER_BYTES + nChunks * CHUNK_BYTES + nStruct * STRUCT_BYTES
    + nVerts * 12 + nTris * 14;
  const pad = (4 - (body % 4)) % 4;
  if (n !== body + pad + 4) fail(`size ${n} B, header says ${body + pad + 4} B`);
  let h = 0x811C9DC5;
  for (let i = 0; i < body + pad; i += STEP_BYTES) { h = fnv1a(u8, Math.min(body + pad, i + STEP_BYTES), i, h); yield; }
  if (dv.getUint32(body + pad, true) !== h) fail('checksum mismatch');
  let o = HEADER_BYTES;
  const chunks = [];
  let expect = 0, prevKey = -Infinity;
  for (let c = 0; c < nChunks; c++) {
    const cx = dv.getInt16(o, true), cz = dv.getInt16(o + 2, true);
    const first = dv.getUint32(o + 4, true), count = dv.getUint32(o + 8, true);
    const key = cz * 65536 + cx;
    if (first !== expect || count < 1 || key <= prevKey) fail(`chunk table corrupt at ${c}`);
    expect += count; prevKey = key;
    chunks.push({ cx, cz, first, count, firstTri: 0, nTris: 0 });
    o += CHUNK_BYTES;
  }
  if (expect !== nStruct) fail(`chunk table covers ${expect} of ${nStruct} structures`);
  const kind = new Uint8Array(nStruct), prov = new Uint8Array(nStruct);
  const srcKind = new Uint8Array(nStruct), srcIndex = new Uint32Array(nStruct);
  const firstVert = new Uint32Array(nStruct), nVert = new Uint32Array(nStruct);
  const firstTri = new Uint32Array(nStruct), nTri = new Uint32Array(nStruct);
  const bbox = new Float32Array(nStruct * 6);
  let fv = 0, ft = 0;
  for (let s = 0; s < nStruct; s++) {
    if ((s & STEP_STRUCTS) === STEP_STRUCTS) yield;
    kind[s] = dv.getUint8(o); prov[s] = dv.getUint8(o + 1);
    srcKind[s] = dv.getUint8(o + 2);
    if (dv.getUint8(o + 3) !== 0) fail(`structure ${s}: reserved byte set`);
    srcIndex[s] = dv.getUint32(o + 4, true);
    firstVert[s] = dv.getUint32(o + 8, true); nVert[s] = dv.getUint32(o + 12, true);
    firstTri[s] = dv.getUint32(o + 16, true); nTri[s] = dv.getUint32(o + 20, true);
    for (let c = 0; c < 6; c++) bbox[s * 6 + c] = dv.getFloat32(o + 24 + c * 4, true);
    if (kind[s] >= KINDS.length) fail(`structure ${s}: kind ${kind[s]}`);
    if (prov[s] >= PROV.length) fail(`structure ${s}: provenance ${prov[s]}`);
    if (srcKind[s] >= SRC.length) fail(`structure ${s}: src kind ${srcKind[s]}`);
    if ((srcKind[s] === 0) !== (srcIndex[s] === SRC_NONE)) fail(`structure ${s}: src kind/index disagree`);
    if (firstVert[s] !== fv || firstTri[s] !== ft || nVert[s] < 3 || nTri[s] < 1) {
      fail(`structure ${s}: vertex/triangle ranges not contiguous`);
    }
    fv += nVert[s]; ft += nTri[s];
    o += STRUCT_BYTES;
  }
  if (fv !== nVerts || ft !== nTris) fail('structure ranges do not sum to the header totals');
  for (const c of chunks) {
    c.firstTri = firstTri[c.first] || 0;
    const l = c.first + c.count - 1;
    c.nTris = firstTri[l] + nTri[l] - c.firstTri;
  }
  const vOff = o, iOff = vOff + nVerts * 12, mOff = iOff + nTris * 12, fOff = mOff + nTris;
  let verts, idx;
  if (_le) {
    verts = new Float32Array(u8.buffer, u8.byteOffset + vOff, nVerts * 3);
    idx = new Uint32Array(u8.buffer, u8.byteOffset + iOff, nTris * 3);
  } else {
    verts = new Float32Array(nVerts * 3);
    idx = new Uint32Array(nTris * 3);
    for (let k = 0; k < nVerts * 3; k++) verts[k] = dv.getFloat32(vOff + k * 4, true);
    for (let k = 0; k < nTris * 3; k++) idx[k] = dv.getUint32(iOff + k * 4, true);
  }
  const mat = u8.subarray(mOff, mOff + nTris);
  const tflags = u8.subarray(fOff, fOff + nTris);
  yield;
  for (let k = 0; k < nVerts * 3; k++) {
    if (!Number.isFinite(verts[k])) fail(`vertex ${(k / 3) | 0} not finite`);
    if ((k & STEP_VALS) === STEP_VALS) yield;
  }
  for (let s = 0; s < nStruct; s++) {
    if ((s & STEP_STRUCTS) === STEP_STRUCTS) yield;
    const v0 = firstVert[s], v1 = v0 + nVert[s];
    // bbox must hold the verts (1 mm slack: it is f32 of f32)
    for (let v = v0; v < v1; v++) {
      for (let c = 0; c < 3; c++) {
        const q = verts[v * 3 + c];
        if (q < bbox[s * 6 + c] - 1e-3 || q > bbox[s * 6 + 3 + c] + 1e-3) fail(`structure ${s}: bbox does not hold vertex ${v}`);
      }
    }
    for (let t = firstTri[s]; t < firstTri[s] + nTri[s]; t++) {
      for (let c = 0; c < 3; c++) {
        const v = idx[t * 3 + c];
        if (v < v0 || v >= v1) fail(`triangle ${t}: vertex ${v} outside structure ${s}`);
      }
      const f = tflags[t], m = mat[t];
      if (f & ~TF_KNOWN) fail(`triangle ${t}: unknown flag bits ${f}`);
      if ((f & TF.NO_COLLIDE) && (f & TF.NO_RENDER)) fail(`triangle ${t}: neither drawn nor collided`);
      if (!(f & TF.NO_COLLIDE) && !(f & TF_ROLE)) fail(`triangle ${t}: collidable with no role (WALK/WALL/CEIL)`);
      if ((f & TF.STAIR) && !(f & TF.WALK)) fail(`triangle ${t}: STAIR without WALK`);
      if (m !== MAT_NONE && m >= MATS.length) fail(`triangle ${t}: material ${m}`);
      if (m === MAT_NONE && !(f & TF.NO_RENDER)) fail(`triangle ${t}: drawn with no material`);
    }
  }
  // every structure is filed under the chunk of its bbox centre (S7 slices by it)
  yield;
  for (const c of chunks) {
    for (let s = c.first; s < c.first + c.count; s++) {
      const [cx, cz] = chunkOf((bbox[s * 6] + bbox[s * 6 + 3]) / 2, (bbox[s * 6 + 2] + bbox[s * 6 + 5]) / 2, chunk);
      if (cx !== c.cx || cz !== c.cz) fail(`structure ${s}: filed under chunk ${c.cx},${c.cz}, centre is in ${cx},${cz}`);
    }
  }
  return {
    version, chunk, nStruct, nVerts, nTris, chunks,
    kind, prov, srcKind, srcIndex, firstVert, nVert, firstTri, nTri, bbox,
    verts, idx, mat, tflags,
    bytes: n,
    // structure owning triangle t (binary search over firstTri)
    structOf(t) {
      let lo = 0, hi = nStruct - 1;
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (firstTri[mid] <= t) lo = mid; else hi = mid - 1;
      }
      return lo;
    },
  };
}

// (owner 2026-10-16: "no bridges should have solid side walls") A RAILING IS
// OPEN: a deck / fill / well rail (world/emit.js emitDecks, world/fills.js,
// world/walls.js; params emit.deck.railing) is ONE collide-only quad per
// straight piece — kind parapet, RAIL_FLAGS — standing on the railing's line
// at its style's height, its material the style's. The client draws posts +
// rails over it (client/src/railing.js), bullets pass it (game/structcol.js).
// railQuad(b0, b1, h, ox, oz) -> its corners [b0, b1, t1, t0], triangles
// (0, 1, 2) (0, 2, 3), ordered so their normal points out along (ox, oz)
// (toward the drop; b0 / b1 swap)
const RAIL_FLAGS = TF.WALL | TF.TWO_SIDED | TF.NO_RENDER;
const RAIL_TRIS = [0, 1, 2, 0, 2, 3];
function railQuad(b0, b1, h, ox, oz) {
  const dx = b1[0] - b0[0], dz = b1[2] - b0[2];
  const [p, q] = -dz * ox + dx * oz < 0 ? [b1, b0] : [b0, b1];
  return [p, q, [q[0], q[1] + h, q[2]], [p[0], p[1] + h, p[2]]];
}
// a railing triangle: kind parapet, collide-only (a drawn parapet is a
// solid one: the flight shells', the architect's, a fill's coping)
function isRailing(kind, f) {
  return kind === KIND_PARAPET && (f & TF.NO_RENDER) !== 0 && (f & TF.WALL) !== 0;
}
// the railings of a loaded set (structures [s0, s1)): one per quad (two
// railing triangles in a row) -> { s, t (its first triangle), a, b (the
// bottom ends), h (the quad's height), ox,
// oz (unit), mat }; a lone or misshapen triangle is counted in bad
function railingsOf(str, s0 = 0, s1 = str.nStruct) {
  const out = [], V = str.verts, I = str.idx;
  let bad = 0;
  const P = (v) => [V[3 * v], V[3 * v + 1], V[3 * v + 2]];
  for (let s = s0; s < s1; s++) {
    if (str.kind[s] !== KIND_PARAPET) continue;
    const t1 = str.firstTri[s] + str.nTri[s];
    for (let t = str.firstTri[s]; t < t1; t++) {
      if (!isRailing(KIND_PARAPET, str.tflags[t])) continue;
      const u = t + 1;
      if (!(u < t1 && isRailing(KIND_PARAPET, str.tflags[u]) && I[3 * u] === I[3 * t] && I[3 * u + 1] === I[3 * t + 2])) { bad++; continue; }
      const a = P(I[3 * t]), b = P(I[3 * t + 1]), tb = P(I[3 * t + 2]), ta = P(I[3 * u + 2]);
      const dx = b[0] - a[0], dz = b[2] - a[2], L = Math.hypot(dx, dz), h = tb[1] - b[1];
      if (!(L > 1e-4) || !(h > 0) || Math.abs(ta[1] - a[1] - h) > 1e-3) { bad++; t = u; continue; }
      out.push({ s, t, a, b, h, ox: -dz / L, oz: dx / L, mat: str.mat[t] });
      t = u;
    }
  }
  return { rails: out, bad };
}

// An empty structure set (baker 2 before S6 emits a file): the collider over
// it answers null/free everywhere.
function emptyStructures() {
  return loadStructures(serializeStructures({ chunk: 0, structures: [] }));
}

module.exports = {
  STR_MAGIC, STR_VERSION, KINDS, PROV, SRC, SRC_NONE, MATS, MAT_NONE, TF, TF_ROLE,
  serializeStructures, loadStructures, loadSteps, emptyStructures, fnv1a,
  RAIL_FLAGS, RAIL_TRIS, railQuad, isRailing, railingsOf,
};
